import {
  Alert,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import { Question } from '../../../../datasources/questions/QuestionsDataSource';
import { Basket } from '../../../../types/basket/Basket';
import { FlowStep, StepConditionMode, describeStep } from './flowSteps';

type FlowStepCardProps = {
  index: number;
  step: FlowStep;
  question: Question | undefined;
  /** Perguntas anteriores com opções, que podem ser usadas na condição. */
  triggers: Question[];
  questionsByCode: Map<string, Question>;
  baskets: Basket[];
  error?: string;
  isLast: boolean;
  disabled: boolean;
  onChange: (step: FlowStep) => void;
  onMove: (offset: number) => void;
  onRemove: () => void;
};

export const FlowStepCard = ({
  index,
  step,
  question,
  triggers,
  questionsByCode,
  baskets,
  error,
  isLast,
  disabled,
  onChange,
  onMove,
  onRemove,
}: FlowStepCardProps) => {
  const isFirst = index === 0;
  const condition = step.condition;
  const trigger = condition
    ? questionsByCode.get(condition.question)
    : undefined;
  const basketOptions =
    question?.options.filter((option) => option.basket_id != null) ?? [];

  function setMode(mode: 'always' | 'conditional'): void {
    if (mode === 'always') {
      onChange({ questionCode: step.questionCode });
      return;
    }
    const lastTrigger = triggers[triggers.length - 1];
    if (!lastTrigger) return;
    onChange({
      questionCode: step.questionCode,
      condition: { question: lastTrigger.code, mode: 'is', values: [] },
    });
  }

  function toggleValue(value: string): void {
    if (!condition) return;
    const values = condition.values.includes(value)
      ? condition.values.filter((item) => item !== value)
      : [...condition.values, value];
    onChange({
      questionCode: step.questionCode,
      condition: { ...condition, values },
    });
  }

  return (
    <Paper
      variant="outlined"
      style={{
        padding: 16,
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        borderColor: error ? '#f59e0b' : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span
          style={{
            flexShrink: 0,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: '#e8f5e9',
            color: '#2e7d32',
            fontWeight: 700,
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {index + 1}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {question?.text ?? 'Esta pergunta não está mais disponível.'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {step.questionCode}
          </Typography>
        </div>
        <div style={{ display: 'flex', flexShrink: 0 }}>
          <Tooltip title="Subir">
            <span>
              <IconButton
                size="small"
                aria-label={`Subir ${step.questionCode}`}
                disabled={disabled || isFirst}
                onClick={() => onMove(-1)}
              >
                <ArrowUp size={16} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Descer">
            <span>
              <IconButton
                size="small"
                aria-label={`Descer ${step.questionCode}`}
                disabled={disabled || isLast}
                onClick={() => onMove(1)}
              >
                <ArrowDown size={16} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remover do fluxo">
            <span>
              <IconButton
                size="small"
                color="error"
                aria-label={`Remover ${step.questionCode} do fluxo`}
                disabled={disabled}
                onClick={onRemove}
              >
                <Trash2 size={16} />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      {basketOptions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {basketOptions.map((option) => (
            <Chip
              key={option.id}
              size="small"
              variant="outlined"
              label={`${option.label} → ${
                baskets.find((basket) => basket.id === option.basket_id)
                  ?.name ?? 'Balaio selecionado'
              }`}
            />
          ))}
        </div>
      )}

      {step.notice && (
        <Alert severity="info" style={{ padding: '0 12px' }}>
          {step.notice}
        </Alert>
      )}

      <div style={{ background: '#f9fafb', borderRadius: 10, padding: 12 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          style={{ marginBottom: 8, fontWeight: 600 }}
        >
          Quando mostrar
        </Typography>

        {isFirst ? (
          <Typography variant="body2" color="text.secondary">
            Primeira pergunta do atendimento: sempre exibida.
          </Typography>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={condition ? 'conditional' : 'always'}
              onChange={(_, value) => value && setMode(value)}
              disabled={disabled}
            >
              <ToggleButton value="always">Sempre</ToggleButton>
              <ToggleButton
                value="conditional"
                disabled={triggers.length === 0}
              >
                Somente quando
              </ToggleButton>
            </ToggleButtonGroup>
            {triggers.length === 0 && !condition && (
              <Typography variant="caption" color="text.secondary">
                Para usar uma condição, coloque antes uma pergunta com opções de
                resposta.
              </Typography>
            )}

            {condition && (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
                    gap: 8,
                  }}
                >
                  <TextField
                    select
                    size="small"
                    label="A resposta da pergunta"
                    value={condition.question}
                    onChange={(event) =>
                      onChange({
                        questionCode: step.questionCode,
                        condition: {
                          question: event.target.value,
                          mode: condition.mode,
                          values: [],
                        },
                      })
                    }
                    disabled={disabled}
                  >
                    {triggers.map((item) => (
                      <MenuItem key={item.code} value={item.code}>
                        {item.text}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Condição"
                    value={condition.mode}
                    onChange={(event) =>
                      onChange({
                        questionCode: step.questionCode,
                        condition: {
                          ...condition,
                          mode: event.target.value as StepConditionMode,
                        },
                      })
                    }
                    disabled={disabled}
                  >
                    <MenuItem value="is">for</MenuItem>
                    <MenuItem value="isNot">não for</MenuItem>
                  </TextField>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(trigger?.options ?? []).map((option) => {
                    const selected = condition.values.includes(option.value);
                    return (
                      <Chip
                        key={option.id}
                        label={option.label}
                        clickable
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => toggleValue(option.value)}
                        disabled={disabled}
                      />
                    );
                  })}
                </div>
                {condition.mode === 'isNot' && (
                  <Typography variant="caption" color="text.secondary">
                    Aparece depois que a pergunta for respondida com outra
                    resposta.
                  </Typography>
                )}
              </>
            )}
          </div>
        )}

        <Typography
          variant="body2"
          style={{
            marginTop: 10,
            color: step.unsupported ? '#b45309' : '#374151',
          }}
        >
          {describeStep(step, questionsByCode)}
        </Typography>
      </div>

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Paper>
  );
};
